import React, { useEffect, useState } from 'react';
import { getConversationPreferences, updateConversationPreferences, deleteAllConversationThreads, getConversationRetentionStatus, downloadAccountData, deleteAccountAndData } from '../lib/conversationThreads';
import { logoutUser, updateUserPreferences } from '../authService';
import { useLocation, useNavigate } from 'react-router-dom';
import { CelestialShell, PageHeader, SystemFooter } from './ui/CelestialShell';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { Link } from 'react-router-dom';

function Toggle({ checked, disabled, onChange, label, detail }) {
  return (
    <label className="br-toggle">
      <span>
        <strong className="br-toggle__label">{label}</strong>
        <span className="br-toggle__detail">{detail}</span>
      </span>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} className="br-switch" aria-label={label} />
    </label>
  );
}

export default function Settings() {
  const { preferences, setPreferences, loading: preferencesLoading } = useUserPreferences();
  const [saveConversationHistory, setSaveConversationHistory] = useState(false);
  const [crossThreadMemory, setCrossThreadMemory] = useState(false);
  const [patternAnalysis, setPatternAnalysis] = useState(false);
  const [growthSummaries, setGrowthSummaries] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteText, setDeleteText] = useState('');
  const [retention, setRetention] = useState(null);
  const [accountDeleteText, setAccountDeleteText] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (new URLSearchParams(location.search).get('history-setup') === 'required') {
      setMessage('History saving stayed off because the privacy setting could not be stored. Review the choice below and save it again.');
    }
  }, [location.search]);

  useEffect(() => {
    if (!preferences) return;
    setPatternAnalysis(Boolean(preferences.tracking_opt_in));
    setGrowthSummaries(Boolean(preferences.tracking_opt_in && preferences.show_growth_summary));
  }, [preferences]);

  useEffect(() => {
    Promise.all([getConversationPreferences(), getConversationRetentionStatus()])
      .then(([preferences, status]) => {
        setSaveConversationHistory(Boolean(preferences.save_conversation_history));
        setCrossThreadMemory(Boolean(preferences.cross_thread_memory_enabled));
        setRetention(status);
      })
      .catch(() => setMessage('Conversation privacy settings are temporarily unavailable.'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      await updateConversationPreferences({
        save_conversation_history: saveConversationHistory,
        cross_thread_memory_enabled: saveConversationHistory && crossThreadMemory,
      });
      setMessage('Conversation settings saved.');
    } catch (_) {
      setMessage('Could not save conversation settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveReflectionPreferences = async () => {
    setSaving(true); setMessage('');
    const nextPreferences = {
      tracking_opt_in: patternAnalysis,
      show_growth_summary: patternAnalysis && growthSummaries,
    };
    try {
      const result = await updateUserPreferences(nextPreferences);
      if (!result?.ok) throw new Error(result?.message || 'preferences_failed');
      setPreferences((current) => ({ ...(current || {}), ...nextPreferences }));
      setMessage('Reflection preferences saved.');
    } catch (_) {
      setMessage('Could not save reflection preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAll = async () => {
    if (deleteText !== 'DELETE') return;
    setSaving(true); setMessage('');
    try {
      await deleteAllConversationThreads();
      setDeleteText('');
      setCrossThreadMemory(false);
      setMessage('All saved conversations were deleted.');
    } catch (_) {
      setMessage('Could not delete saved conversations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    setSaving(true); setMessage('');
    try { await downloadAccountData(); setMessage('Your data export is downloading.'); }
    catch (_) { setMessage('Could not export your data. Please try again.'); }
    finally { setSaving(false); }
  };

  const deleteAccount = async () => {
    if (accountDeleteText !== 'DELETE MY ACCOUNT' || !currentPassword) return;
    setSaving(true); setMessage('');
    try {
      await deleteAccountAndData(currentPassword);
      await logoutUser();
      navigate('/login', { replace: true });
    } catch (err) {
      setMessage(err?.message === 'reauthentication_required'
        ? 'Enter your current password to confirm account deletion.'
        : 'Could not delete the account. No further action was taken.');
    } finally { setSaving(false); }
  };

  return (
    <CelestialShell authenticated>
      <main className="br-container br-container--narrow">
        <PageHeader eyebrow="Settings" title="Account controls" description={retention?.retention === 'account' ? 'Manage conversation preferences, privacy, and account data.' : 'Manage conversation preferences, privacy, and account access.'} />

        <nav className="br-settings-index br-glass" aria-label="Settings sections">
          <a href="#account">Account</a><a href="#conversation">AI and conversation</a><a href="#privacy-data">Privacy and data</a>
        </nav>

        <section id="account" className="br-panel br-glass br-section">
          <div className="br-section__header"><p className="br-eyebrow">Account</p><h2 className="br-section__title">Profile and access</h2><p className="br-section__copy">Review the identity connected to this private account.</p></div>
          <Link to="/profile" className="br-button">Open profile</Link>
        </section>

        <section id="conversation" className="br-panel br-glass br-section">
          <div className="br-section__header"><p className="br-eyebrow">AI and conversation</p><h2 className="br-section__title">Reflection and continuity</h2><p className="br-section__copy">Choose whether BrightResolve may form private insights or recall eligible details between conversations.</p></div>
          <div className="br-settings-group">
            <h3 className="br-settings-group__title">Insights</h3>
          <div className="br-settings-list">
            <Toggle
              checked={patternAnalysis}
              disabled={preferencesLoading || saving}
              onChange={() => {
                setPatternAnalysis((value) => {
                  if (value) setGrowthSummaries(false);
                  return !value;
                });
              }}
              label="Allow private pattern analysis"
              detail="When enabled, BrightResolve may identify recurring themes across eligible conversations for your eyes only."
            />
            <Toggle
              checked={patternAnalysis && growthSummaries}
              disabled={!patternAnalysis || preferencesLoading || saving}
              onChange={() => setGrowthSummaries((value) => !value)}
              label="Show Insights in my workspace"
              detail="Controls whether considered reflections appear as a dedicated space in navigation."
            />
          </div>
          <button type="button" onClick={saveReflectionPreferences} disabled={preferencesLoading || saving} className="br-button br-button--primary" style={{ marginTop: '1.2rem' }}>
            {saving ? 'Saving...' : 'Save reflection preferences'}
          </button>
          </div>
          <div className="br-settings-group">
            <h3 className="br-settings-group__title">Conversation history</h3>
          <div className="br-settings-list">
            <Toggle
              checked={saveConversationHistory}
              disabled={loading || saving}
              onChange={() => setSaveConversationHistory((value) => {
                if (value) setCrossThreadMemory(false);
                return !value;
              })}
              label="Save conversation history"
              detail="Off by default. When enabled, new messages are stored so you can reopen them. Turning it off stops future message storage but does not delete chats already saved."
            />
          </div>
          <p className="br-section__copy" style={{ marginTop: '.85rem' }}>Saved chats currently remain until you delete them. BrightResolve’s proposed automated window is up to 30 days, but it will not be activated until the documented retention and legal-hold review is approved.</p>
          </div>
          <div className="br-settings-group">
            <h3 className="br-settings-group__title">Memory</h3>
          <div className="br-settings-list">
            <Toggle
              checked={crossThreadMemory}
              disabled={!saveConversationHistory || loading || saving}
              onChange={() => setCrossThreadMemory((value) => !value)}
              label="Recall relevant details across my chats"
              detail="Available only when history saving is on. Sonny may use relevant details from your other saved chats to maintain continuity. It does not expose conversations to other accounts."
            />
          </div>
          <button type="button" onClick={save} disabled={loading || saving} className="br-button br-button--primary" style={{ marginTop: '1.2rem' }}>
            {saving ? 'Saving...' : 'Save conversation settings'}
          </button>
          </div>
        </section>

        <section id="privacy-data" className="br-panel br-glass br-section">
          <div className="br-section__header"><p className="br-eyebrow">Privacy and data</p><h2 className="br-section__title">Your archive</h2><p className="br-section__copy">Download a copy of supported account data without changing or removing anything.</p></div>
          <button type="button" onClick={exportData} disabled={saving || retention?.retention !== 'account'} className="br-button">Download my data</button>
        </section>

        <section className="br-panel br-glass br-section br-danger-zone">
          <div className="br-section__header"><p className="br-eyebrow" style={{ color: 'rgba(var(--br-danger),.68)' }}>Permanent actions</p><h2 className="br-section__title">Deletion</h2><p className="br-section__copy">These actions cannot be undone.</p></div>
          <div className="br-settings-group">
          <h3 className="br-settings-group__title">Delete all conversations</h3>
          <p className="br-section__copy">Removes every saved chat and message without deleting your account.</p>
          <label className="mt-4 block text-sm text-white/70" htmlFor="delete-conversations-confirmation">Type <strong>DELETE</strong> to enable this action.</label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <input id="delete-conversations-confirmation" value={deleteText} onChange={(event) => setDeleteText(event.target.value)} placeholder="DELETE" className="br-input min-w-0 flex-1" />
            <button type="button" onClick={deleteAll} disabled={saving || deleteText !== 'DELETE'} className="br-button br-button--danger">Delete all chats</button>
          </div>
          </div>
          <div className="br-settings-group">
          <h3 className="br-settings-group__title">Delete account and all data</h3>
          <p className="br-section__copy">Removes your account, conversations, and stored data. Confirm the phrase and your current password.</p>
          <div className="mt-4 space-y-3">
            <input value={accountDeleteText} onChange={(event) => setAccountDeleteText(event.target.value)} placeholder="DELETE MY ACCOUNT" aria-label="Account deletion confirmation" className="br-input" />
            <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} placeholder="Current password" autoComplete="current-password" aria-label="Current password" className="br-input" />
            <button type="button" onClick={deleteAccount} disabled={saving || retention?.retention !== 'account' || accountDeleteText !== 'DELETE MY ACCOUNT' || !currentPassword} className="br-button br-button--danger">Delete account</button>
          </div>
          </div>
        </section>

        {message && <p role="status" className="br-status">{message}</p>}
        <SystemFooter />
      </main>
    </CelestialShell>
  );
}
